import * as THREE from "three";
import { addStylizedShading } from "../stylizedShading";
import { FLORA_SHARED } from "./material";
import { WIND_GLSL, WIND_UNIFORMS } from "./wind";
import { grassInstanceCount, grassDensityEnds } from './grassDensity';
import { buildGrassData, splitGrassData, type GrassFieldData, type GrassSource } from './grassData';
export { BLADES_PER_CLUMP, type GrassClump, type GrassSource } from './grassData';

/**
 * Grass as BLADES.
 *
 * What was here before was a clump mesh — a dozen little cones welded together,
 * scattered a couple of hundred times per tile. It reads as "some triangles on
 * the ground" because that is what it is: the shape never changes, so it cannot
 * bend, and there are too few of them to close into a sward.
 *
 * This is the reference pen's model instead (docs/reference/painterly.html,
 * GRASS_VS): every blade is a quadratic Bézier with three control points — root,
 * midpoint, tip — evaluated in the vertex shader, so the whole blade CURVES
 * under the wind rather than pivoting. The tip travels furthest, the root stays
 * planted, and the curve keeps its arc length while it bends (the correction
 * from Jahrmann & Wimmer's grass paper), which is what stops a bent blade also
 * looking stretched.
 *
 * THE CPU PROBLEM, AND THE CLUMPS THAT SOLVE IT. The pen reads ground height
 * from a heightmap TEXTURE in the vertex shader, so blades cost it nothing to
 * place. Our terrain is a procedural sphere sampled on the CPU, and a sampler
 * call per blade would put tens of thousands of them on the main thread per
 * tile. So blades are built in CLUMPS: the ground is sampled ONCE per clump and
 * ~20 blades share that result, spread over half a metre. The sampler is called
 * about as often as it was for the old tufts, and roughly sixty times as many
 * blades come out. It also happens to be what real grass does — tussocks, not an
 * even lawn — which the pen gets from a separate meadow texture.
 *
 * DENSITY IS RESOLVED PER BLADE, ON THE GPU. Every blade is built for the
 * closest the camera will get; the shader then collapses a distance-dependent
 * fraction of them to zero area (which costs a discarded triangle, not a
 * fragment). That gives a smooth radial density gradient with no tile banding,
 * and it is why one tile can carry fifteen thousand blades without the far ones
 * being drawn.
 */

/** Segments along a blade. Three still reads as a curve and is 8 verts, not 10. */
const SEGMENTS = 3;

/** Live tuning — `__hl.grass` in the console. */
export const GRASS_TUNING = {
  /** Distance at which the per-blade density law starts thinning, metres. */
  nearFull: 20,
  /** Master blade-count multiplier applied at build time. */
  density: 1.5,
  /** Height multiplier. */
  height: 1.5,
};

const SHARED = {
  uGrassNear: { value: GRASS_TUNING.nearFull },
};

export function updateGrassUniforms(): void {
  SHARED.uGrassNear.value = Math.max(4, GRASS_TUNING.nearFull);
}

/**
 * The blade template: a strip of quads, `position.x` across (-0.5..0.5) and
 * `position.y` along (0..1). Shared by every blade in the world; everything that
 * differs between blades rides in the instance attributes.
 */
function bladeTemplate(): THREE.BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= SEGMENTS; i++) {
    const t = i / SEGMENTS;
    pos.push(-0.5, t, 0, 0.5, t, 0);
  }
  for (let i = 0; i < SEGMENTS; i++) {
    const a = i * 2;
    idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  return g;
}

let TEMPLATE: THREE.BufferGeometry | null = null;

const VERT_DECLS = /* glsl */ `
attribute vec3 iRoot;
attribute vec4 iData;   // x yaw, y height, z half-width, w per-blade random
attribute vec3 iTint;
uniform vec3 uGrassOrigin;
uniform float uGrassNear;
uniform float uFadeFar;
uniform float uFadeScale;
uniform vec3 uFloraEye;
uniform float uBirth;
uniform float uWind;
varying float vFloraFade;
varying float vBladeT;
varying vec3 vGrassUpView;
`;

/**
 * Everything happens here, in <beginnormal_vertex>, because three runs it BEFORE
 * <begin_vertex> and the blade's normal and its position come out of the same
 * construction. The results are stashed and <begin_vertex> just takes them.
 */
const VERT_BODY = /* glsl */ `
  vec3 gBladePos;
  vec3 gBladeNrm;
  float gBladeCut = 0.0;
  {
    vec3 up = normalize(iRoot + uGrassOrigin);
    vGrassUpView = normalize(normalMatrix * up);
    float rnd = iData.w;

    // ── density, resolved per blade against its OWN distance ───────────────
    // The tile was built at full density for the near view; this rejects the
    // surplus. A hard cut makes a blade POP as you walk toward it, so the last
    // part of the acceptance window is a growth ramp and the blade rises out of
    // the sward instead.
    vec4 gWorld = modelMatrix * vec4(iRoot, 1.0);
    float gDist = distance(gWorld.xyz, uFloraEye);
    float xr = min(uGrassNear / max(gDist, uGrassNear), 1.0);
    // Keep a readable sward through the walking/middle-distance transition.
    // The old inverse-distance^2.5 left only 3% of blades at 80 m.
    float keep = xr * sqrt(xr);
    float pick = fract(rnd * 97.31);
    float grow = clamp((keep - pick) / max(keep * 0.25, 1e-4), 0.0, 1.0);
    gBladeCut = grow;

    float h = iData.y * grow;
    // EARLY OUT, and this is the difference between dense grass being affordable
    // and not. Collapsing a rejected blade at the END still runs the wind field,
    // the Bézier and the frame construction for all of its vertices — thinning
    // on the GPU then saves fill and no vertex work at all, which is exactly how
    // a blade field goes from fill-bound to vertex-bound. Everything expensive
    // below is behind this test, so a distant tile costs a distance and a hash.
    if (grow <= 0.0) {
      gBladePos = iRoot;
      gBladeNrm = up;
      vFloraFade = 0.0;
      vBladeT = 0.0;
      vColor = vec3(0.0);
    } else {
    float wid = iData.z;
    // Angular floor: never let a blade fall below about a pixel wide, or a
    // distant sward turns into shimmering dashes.
    wid = max(wid, gDist * 0.00035);

    // ── frame ──────────────────────────────────────────────────────────────
    vec3 ref = abs(up.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 ex = normalize(cross(ref, up));
    vec3 ez = cross(up, ex);
    float yaw = iData.x;
    vec3 side = ex * cos(yaw) + ez * sin(yaw);
    vec3 front = cross(side, up);

    vec3 p0 = iRoot;
    // The tip a blade would reach with no wind: nearly straight up, with a
    // little lean so a still meadow is not a bed of nails.
    vec3 v2 = p0 + up * (h * 0.84) + front * (h * (0.28 + rnd * 0.32));

    // ── wind ───────────────────────────────────────────────────────────────
    vec4 Wf = hlWindAt(iRoot + uGrassOrigin);
    vec3 wind = Wf.xyz - up * dot(Wf.xyz, up);
    float stiff = 0.55 + rnd * 0.5;
    vec3 dir0 = normalize(v2 - p0);
    // A blade edge-on to the flow catches less of it than one face-on.
    float align = 1.0 - abs(dot(normalize(wind + vec3(1e-5)), dir0));
    v2 += wind * ((0.30 + 0.95 * align) * 0.028 * uWindGain * h / stiff);
    // Ringing: a gust front leaves the blade quivering at its own frequency.
    float ph = rnd * 6.2831853;
    vec3 wdir = normalize(wind + vec3(1e-5));
    v2 += wdir * (sin(uWind * (4.5 + rnd * 3.0) + ph) * Wf.w * h * 0.055 * uWindGain);
    // Never frozen: a low flutter is always present.
    v2 += side * (sin(uWind * (7.4 * (0.65 + rnd)) + ph * 2.3) * h * 0.012);

    // ── the walker parts the grass ─────────────────────────────────────────
    if (uWindPush > 0.0) {
      vec3 d = iRoot - (uWindPlayer - uGrassOrigin);
      vec3 dFlat = d - up * dot(d, up);
      float pd = length(dFlat);
      if (pd < uWindPushRadius * 1.2) {
        float vert = 1.0 - smoothstep(1.2, 2.6, abs(dot(d, up)));
        float push = smoothstep(uWindPushRadius, uWindPushRadius * 0.1, pd) * vert * uWindPush;
        v2 += (dFlat / max(pd, 0.02)) * (push * h * 0.8) - up * (push * h * 0.25);
      }
    }

    // ── keep the blade's own length while it bends ─────────────────────────
    // Without this a bent blade is also a LONGER one, and a gust visibly grows
    // the meadow. Jahrmann & Wimmer's correction: never let the tip go below the
    // ground, place the midpoint from how far the tip has travelled sideways,
    // then rescale the whole curve back to the blade's arc length.
    v2 -= up * min(dot(up, v2 - p0), 0.0);
    vec3 d20 = v2 - p0;
    float lproj = length(d20 - up * dot(d20, up));
    vec3 v1 = p0 + up * (h * max(1.0 - lproj / max(h, 1e-4), 0.05 * max(lproj / max(h, 1e-4), 1.0)));
    float L0 = length(v2 - p0);
    float L1 = length(v1 - p0) + length(v2 - v1);
    float L = (2.0 * L0 + L1) / 3.0;
    float rr = h / max(L, 1e-4);
    v1 = p0 + rr * (v1 - p0);
    v2 = v1 + rr * (v2 - v1);

    // ── evaluate ───────────────────────────────────────────────────────────
    float t = position.y;
    vec3 qa = mix(p0, v1, t);
    vec3 qb = mix(v1, v2, t);
    vec3 c = mix(qa, qb, t);
    vec3 tang = normalize(qb - qa + up * 1e-5);
    // Taper: widest just above the root, drawn to a point at the tip.
    float wprof = sqrt(max(1.0 - t, 0.0)) * (0.60 + 0.42 * smoothstep(0.0, 0.16, t));
    vec3 sideW = normalize(side - tang * dot(side, tang) + vec3(1e-6));
    gBladePos = c + sideW * (position.x * wid * wprof * 2.0);

    // A curved cross-section, so a blade shades like a rolled leaf instead of a
    // flat ribbon — this is most of what keeps a sward from reading as cardboard.
    vec3 faceN = normalize(cross(sideW, tang));
    gBladeNrm = normalize(faceN + sideW * (position.x * 2.0) * 0.62);

    vBladeT = t;
    vColor = iTint * (0.98 + 0.12 * t) * (0.97 + 0.06 * rnd);

    vFloraFade = smoothstep(0.0, 1.2, uWind - uBirth);
    float fadeFarD = uFadeFar * uFadeScale;
    if (uFadeFar > 0.0) vFloraFade *= 1.0 - smoothstep(fadeFarD - 40.0 * uFadeScale, fadeFarD, gDist);
    }
  }
  vec3 objectNormal = gBladeNrm;
`;

const FRAG_DECLS = /* glsl */ `
varying float vFloraFade;
varying float vBladeT;
varying vec3 vGrassUpView;
`;

const FRAG_BODY = /* glsl */ `
  if (vFloraFade < 0.999 && fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) >= vFloraFade) discard;
`;

export interface GrassFieldOpts {
  birth: number;
  fadeFar: number;
}

/** The material for one grass tile. Shares programs; only the fade uniforms differ. */
function makeGrassMaterial(opts: GrassFieldOpts, origin: THREE.Vector3): THREE.MeshLambertMaterial {
  const mat = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide });
  const u = {
    uGrassOrigin: { value: origin },
    uBirth: { value: opts.birth },
    uFadeFar: { value: opts.fadeFar },
  };
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, WIND_UNIFORMS, SHARED, u, {
      uWind: FLORA_SHARED.WIND,
      uFadeScale: FLORA_SHARED.FADE_SCALE,
      uFloraEye: FLORA_SHARED.EYE,
    });
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\n" + VERT_DECLS + WIND_GLSL)
      // The construction replaces beginnormal_vertex outright — it DEFINES
      // objectNormal, which that chunk would otherwise declare a second time.
      .replace("#include <beginnormal_vertex>", VERT_BODY)
      .replace("#include <begin_vertex>", "vec3 transformed = gBladePos;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\n" + FRAG_DECLS)
      .replace("#include <clipping_planes_fragment>", "#include <clipping_planes_fragment>\n" + FRAG_BODY)
      .replace("#include <normal_fragment_begin>", `#include <normal_fragment_begin>
        // Root lighting follows the ground on both sides of the blade.
        normal = normalize(mix(normalize(vGrassUpView), normal, .65 * smoothstep(0., .75, vBladeT)));
      `);
  };
  mat.customProgramCacheKey = () => "grass-blades-v3";
  // ink: 0 — a meadow's depth silhouette is per-blade chaos; inking it strobes.
  addStylizedShading(mat, { weatherGround: true, paintLocal: true, ink: 0 });
  return mat;
}

/** Synchronous authoring/test entry point. Live terrain uses worker-built data. */
export function buildGrassField(clumps: number, rng: () => number, src: GrassSource, opts: GrassFieldOpts): THREE.Mesh | null {
  const field = buildGrassData(clumps, rng, src, GRASS_TUNING);
  return field ? mountGrassField(field, opts) : null;
}

export function mountGrassField(field: GrassFieldData, opts: GrassFieldOpts, material?: THREE.Material): THREE.Mesh {
  if (!TEMPLATE) TEMPLATE = bladeTemplate();
  const geo = new THREE.InstancedBufferGeometry();
  // Tile disposal owns its template attributes as well as its instance buffers.
  geo.index = TEMPLATE.index!.clone();
  geo.setAttribute('position', TEMPLATE.getAttribute('position').clone());
  geo.setAttribute('iRoot', new THREE.InstancedBufferAttribute(field.roots, 3));
  geo.setAttribute('iData', new THREE.InstancedBufferAttribute(field.data, 4));
  geo.setAttribute('iTint', new THREE.InstancedBufferAttribute(field.tints, 3));
  geo.instanceCount = field.roots.length / 3;
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3().fromArray(field.bounds.center), field.bounds.radius);
  geo.boundingBox = new THREE.Box3(new THREE.Vector3().fromArray(field.bounds.min), new THREE.Vector3().fromArray(field.bounds.max));
  const origin = new THREE.Vector3().fromArray(field.origin);
  const mesh = new THREE.Mesh(geo, material ?? makeGrassMaterial(opts, origin));
  mesh.position.copy(origin); mesh.receiveShadow = true;
  mesh.userData.blades = geo.instanceCount; mesh.userData.fadeFar = opts.fadeFar; mesh.userData.ownGeometry = true;
  const world = new THREE.Sphere();
  mesh.onBeforeRender = () => {
    world.copy(geo.boundingSphere!).applyMatrix4(mesh.matrixWorld);
    const nearest = Math.max(0, world.distanceToPoint(FLORA_SHARED.EYE.value));
    geo.instanceCount = opts.fadeFar && nearest > opts.fadeFar * FLORA_SHARED.FADE_SCALE.value ? 0
      : grassInstanceCount(field.densityEnds, nearest, SHARED.uGrassNear.value);
  };
  return mesh;
}

/** GPU adoption only: all sampling, sorting, partitioning and bounds are done. */
export function mountGrassFields(fields: readonly GrassFieldData[], opts: GrassFieldOpts): THREE.Mesh[] {
  if (!fields.length) return [];
  const material = makeGrassMaterial(opts, new THREE.Vector3().fromArray(fields[0].origin));
  return fields.map(field => mountGrassField(field, opts, material));
}

/** Compatibility for synchronous authoring tools; live terrain partitions off-thread. */
export function splitGrassField(field: THREE.Mesh): THREE.Mesh[] {
  const g = field.geometry;
  const roots = g.getAttribute('iRoot').array as Float32Array, data = g.getAttribute('iData').array as Float32Array;
  const b = g.boundingBox!, s = g.boundingSphere!;
  const fields = splitGrassData({ origin: field.position.toArray(), roots, data,
    tints: g.getAttribute('iTint').array as Float32Array, densityEnds: grassDensityEnds(data, roots.length / 3),
    bounds: { min: b.min.toArray(), max: b.max.toArray(), center: s.center.toArray(), radius: s.radius } });
  if (fields.length === 1) return [field];
  const meshes = fields.map(data => mountGrassField(data, { birth: 0, fadeFar: field.userData.fadeFar }, field.material as THREE.Material));
  g.dispose();
  return meshes;
}
